import events from 'components/arrp/dashboard/possibleOnDeckEvents'
import roles from '$app/auth/roles.js'
import getConfigs from '$app/configs'
import setMetadata from '$app/set-metadata'

const Home = () => import('components/home/home.vue').catch(handleImportFailure)
const Search = () => import('components/search/search.vue').catch(handleImportFailure)
const Report = () => import('components/report/provider-readiness-report.vue').catch(handleImportFailure)
const Contact = () => import('components/contact/contact.vue').catch(handleImportFailure)
const Login = () => import('components/login.vue').catch(handleImportFailure)
const RedirectNotice = () => import('components/redirect-notice.vue').catch(handleImportFailure)
const ProviderHome = () => import(/* webpackChunkName: "provider/home" */ 'components/provider-home/provider-home.vue').catch(handleImportFailure)
const ImprovementPlan = () => import(/* webpackChunkName: "provider/ip" */ 'components/improvement-plan/improvement-plan.vue').catch(handleImportFailure)
const Profile = () => import('components/profile/profile.vue').catch(handleImportFailure)
const VerificationChildrenServed = () => import(/* webpackChunkName: "provider/vcs" */ 'components/vcs/verification-children-served.vue').catch(handleImportFailure)
const GoodCauseExemption = () => import(/* webpackChunkName: "provider/gce" */ 'components/gce/good-cause-exemption.vue').catch(handleImportFailure)
const Acknowledgement = () => import(/* webpackChunkName: "provider/acknowledgement" */ 'components/provider-acknowledgement/acknowledgement.vue').catch(handleImportFailure)
const DisputeForm = () => import(/* webpackChunkName: "provider/dispute" */ 'components/dispute/dispute-form.vue').catch(handleImportFailure)
const ArrpDashboard = () => import(/* webpackChunkName: "arrp/dashboard" */ 'components/arrp/dashboard/dashboard.vue').catch(handleImportFailure)
const ArrpProviderStaging = () => import(/* webpackChunkName: "arrp/provider-staging" */ 'components/arrp/provider-staging/data-table.vue').catch(handleImportFailure)
const ArrpEnrollmentStaging = () => import(/* webpackChunkName: "arrp/enrollment-staging" */ 'components/arrp/enrollment-staging/data-table.vue').catch(handleImportFailure)
const ArrpFlkrsStaging = () => import(/* webpackChunkName: "arrp/flkrs-staging" */ 'components/arrp/flkrs-staging/data-table.vue').catch(handleImportFailure)
const ArrpProgramYears = () => import(/* webpackChunkName: "arrp/program-year" */ 'components/arrp/program-year/program-year-table.vue').catch(handleImportFailure)
const ArrpProviders = () => import(/* webpackChunkName: "arrp/providers" */ 'components/arrp/providers/data-table.vue').catch(handleImportFailure)
const ArrpEnrollments = () => import(/* webpackChunkName: "arrp/enrollments" */ 'components/arrp/enrollments/data-table.vue').catch(handleImportFailure)
const ArrpManualMatch = () => import(/* webpackChunkName: "arrp/manual-match" */ 'components/arrp/manual-match/manual-match.vue').catch(handleImportFailure)

function handleImportFailure(err) {
  console.error(err);
  window.alert('Error: unable to load resource');
  window.location.href = window.base;
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    beforeEnter: (to, from, next) => {
      const content = 'Welcome to the Florida Division of Early Learning\'s VPK Provider Readiness Rate website. This site provides information about how well VPK providers in the state of Florida prepared four-year-olds for kindergarten.';
      setMetadata('Readiness Rates Home | FL Voluntary Prekindergarten | OEL', [
        { name: 'description', content },
        { property: 'og:description', content }
      ]);
      next();
    },
    meta: {
      nav: {
        title: 'VPK Readiness Rate Home',
        label: 'Home',
        icon: 'home'
      }
    }
  },
  {
    path: '/search',
    name: 'Search',
    component: Search,
    props: (route) => ({ queryProps: route.query }),
    beforeEnter: (to, from, next) => {
      const content = 'Search for Readiness Rates achieved by VPK providers. Readiness Rate measures how well the provider prepared four-year-olds for kindergarten. The Readiness Rate is expressed as a percentage of the provider\'s students who were assessed as ready for kindergarten.';
      setMetadata('Readiness Rates Search | FL Voluntary Prekindergarten | OEL', [
        { name: 'description', content },
        { property: 'og:description', content }
      ]);
      next();
    },
    meta: {
      nav: {
        title: 'Search for Readiness Rate',
        label: 'Search',
        icon: 'search'
      }
    }
  },
  {
    path: '/report',
    name: 'Readiness Rates Report',
    component: Report,
    props: (route) => ({ providerId: route.query.id }),
    beforeEnter: (to, from, next) => {
      const content = 'View a report of Readiness Rates achieved by VPK providers.';
      setMetadata('Readiness Rates Report | FL Voluntary Prekindergarten | OEL', [
        { name: 'description', content },
        { property: 'og:description', content }
      ]);
      next();
    },
    meta: {}
  },
  {
    path: '/contact',
    name: 'Contact Us',
    component: Contact,
    beforeEnter: (to, from, next) => {
      getConfigs().then(configs => {
        const content = `For more information about Florida's VPK Program, contact the Division of Early Learning by calling ${configs.phoneNumber} or via e-mail at vpkquestions@oel.myflorida.com.`;
        setMetadata('Readiness Rates Contact | FL Voluntary Prekindergarten | OEL', [
          { name: 'description', content },
          { property: 'og:description', content }
        ]);
        next();
      });
    },
    meta: {
      nav: {
        title: 'Contact Us',
        label: 'Contact Us',
        icon: 'contact_support'
      }
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    beforeEnter: (to, from, next) => {
      setMetadata('Readiness Rates Login | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {}
  },
  {
    path: '/redirected:url(.*)',
    name: 'Redirected',
    component: RedirectNotice,
    beforeEnter: (to, from, next) => {
      setMetadata('Readiness Rates Home | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {}
  },
  {
    path: '/provider',
    name: 'Provider Home',
    component: ProviderHome,
    beforeEnter: (to, from, next) => {
      setMetadata('Provider Home | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Provider',
        title: 'Provider Home',
        label: 'Provider',
        icon: 'home'
      },
      auth: (checkAuth) => checkAuth([roles.provider])
    }
  },
  {
    path: '/provider/vcs',
    name: 'Verification of Children Served',
    component: VerificationChildrenServed,
    beforeEnter: (to, from, next) => {
      setMetadata('Verification of Children Served | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Provider',
        title: 'Verification of Children Served',
        label: 'VCS',
        icon: 'how_to_reg'
      },
      auth: (checkAuth) => checkAuth([roles.provider])
    }
  },
  {
    path: '/provider/acknowledgement',
    name: 'Acknowledgement',
    component: Acknowledgement,
    beforeEnter: (to, from, next) => {
      setMetadata('Readiness Rate Acknowledgement | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Provider',
        title: 'Readiness Rate Acknowledgement',
        label: 'Acknowledgement',
        icon: 'create'
      },
      auth: (checkAuth) => checkAuth([roles.provider])
    }
  },
  {
    path: '/provider/dispute',
    name: 'Dispute Form',
    component: DisputeForm,
    beforeEnter: (to, from, next) => {
      setMetadata('Dispute Form | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Provider',
        title: 'Dispute Form',
        label: 'Dispute',
        icon: 'report_problem'
      },
      auth: (checkAuth) => checkAuth([roles.provider])
    }
  },
  {
    path: '/provider/gce',
    name: 'Good Cause Exemption',
    component: GoodCauseExemption,
    beforeEnter: (to, from, next) => {
      setMetadata('Good Cause Exemption | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Provider',
        title: 'Good Cause Exemption',
        label: 'GCE',
        icon: 'check_box'
      },
      auth: (checkAuth) => checkAuth([roles.provider])
    }
  },
  {
    path: '/provider/ip',
    name: 'Improvement Plan',
    component: ImprovementPlan,
    beforeEnter: (to, from, next) => {
      setMetadata('Provider Improvement Plan | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Provider',
        title: 'Improvement Plan',
        label: 'Improvement Plan',
        icon: 'trending_up'
      },
      auth: (checkAuth) => checkAuth([roles.provider])
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    beforeEnter: (to, from, next) => {
      setMetadata('Account Profile | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: false && {
        title: 'Account Profile',
        label: 'Profile',
        icon: 'person'
      }
    }
  },
  {
    path: '/arrp/dashboard',
    alias: '/arrp/',
    name: 'ARRP',
    component: ArrpDashboard,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP Dashboard | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'Dashboard',
        label: 'Dashboard',
        icon: 'dashboard'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.createProgramYear,
        events.completeProgramYear,
        events.providerFileUpload,
        events.enrollmentFileUpload,
        events.startPhaseIIDisabledProvider,
        events.startPhaseIIDisabledEnrollment,
        events.startPhaseIIEnabled,
        events.flkrsFileUpload,
        events.enrollmentToFLKRSAutoMatch,
        events.calculatePreliminaryRR,
        events.publishPreliminaryRR,
        events.calculateFinalRR,
        events.publishFinalRR
      ]
    }
  },
  {
    path: '/arrp/programYear',
    name: 'Program Years',
    component: ArrpProgramYears,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP Program Years | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'Program Years',
        label: 'Program Years',
        icon: 'date_range'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.createProgramYear,
        events.completeProgramYear,
        events.providerFileUpload,
        events.enrollmentFileUpload,
        events.startPhaseIIDisabledProvider,
        events.startPhaseIIDisabledEnrollment,
        events.startPhaseIIEnabled,
        events.flkrsFileUpload,
        events.enrollmentToFLKRSAutoMatch,
        events.calculatePreliminaryRR,
        events.publishPreliminaryRR,
        events.calculateFinalRR,
        events.publishFinalRR
      ]
    }
  },
  {
    path: '/arrp/providerStaging',
    name: 'Provider File',
    component: ArrpProviderStaging,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP Provider Staging | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'Providers',
        label: 'Provider File',
        icon: 'school'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.providerFileUpload,
        events.enrollmentFileUpload,
        events.startPhaseIIDisabledProvider,
        events.startPhaseIIDisabledEnrollment,
        events.startPhaseIIEnabled
      ]
    }
  },
  {
    path: '/arrp/providers',
    name: 'Providers',
    component: ArrpProviders,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP Providers | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'Providers',
        label: 'Providers',
        icon: 'school'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.flkrsFileUpload,
        events.completeProgramYear,
        events.enrollmentToFLKRSAutoMatch,
        events.calculatePreliminaryRR,
        events.publishPreliminaryRR,
        events.calculateFinalRR,
        events.publishFinalRR,
        events.createProgramYear
      ]
    }
  },
  {
    path: '/arrp/enrollmentStaging',
    name: 'Enrollment File',
    component: ArrpEnrollmentStaging,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP Enrollment Staging | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'Enrollments',
        label: 'Enrollment File',
        icon: 'group'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.providerFileUpload,
        events.enrollmentFileUpload,
        events.startPhaseIIDisabledProvider,
        events.startPhaseIIDisabledEnrollment,
        events.startPhaseIIEnabled
      ]
    }
  },
  {
    path: '/arrp/enrollments',
    name: 'Enrollments',
    component: ArrpEnrollments,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP Enrollments | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'Enrollments',
        label: 'Enrollments',
        icon: 'group'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.completeProgramYear,
        events.flkrsFileUpload,
        events.enrollmentToFLKRSAutoMatch,
        events.calculatePreliminaryRR,
        events.publishPreliminaryRR,
        events.calculateFinalRR,
        events.publishFinalRR,
        events.createProgramYear
      ]
    }
  },
  {
    path: '/arrp/flkrs',
    name: 'FLKRS',
    component: ArrpFlkrsStaging,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP FLKRS File | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'FLKRS',
        label: 'FLKRS',
        icon: 'assessment'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.completeProgramYear,
        events.flkrsFileUpload,
        events.enrollmentToFLKRSAutoMatch,
        events.calculatePreliminaryRR,
        events.publishPreliminaryRR,
        events.calculateFinalRR,
        events.publishFinalRR,
        events.createProgramYear
      ]
    }
  },
  {
    path: '/arrp/manual-match',
    name: 'Manual Match',
    component: ArrpManualMatch,
    beforeEnter: (to, from, next) => {
      setMetadata('ARRP Manual Match | FL Voluntary Prekindergarten | OEL', []);
      next();
    },
    meta: {
      nav: {
        menu: 'Rate Manager',
        title: 'Manual Match',
        label: 'Manual Match',
        icon: 'swap_horiz'
      },
      auth: (checkAuth) => checkAuth([roles.arrpAdmin, roles.arrpAnalyst, roles.arrpViewer]),
      events: [
        events.completeProgramYear,
        events.calculatePreliminaryRR,
        events.publishPreliminaryRR,
        events.calculateFinalRR,
        events.publishFinalRR
      ]
    }
  }
];

export default routes
